import { getTimeFormat } from "@/helpers"
import type { Locales } from "@/i18n/i18n-types"
import z from "zod"
import type { District, School } from "@/models"

const AnnouncementPictureInfoSchema = z.object({
  pictureUrl: z.string(),
  picture800Url: z.string(),
  picture500Url: z.string(),
  picture250Url: z.string()
})

export class AnnouncementPictureInfo {
  constructor(data: z.infer<typeof AnnouncementPictureInfoSchema>) {
    Object.assign(this, data)
  }

  public readonly pictureUrl!: string
  public readonly picture800Url!: string
  public readonly picture500Url!: string
  public readonly picture250Url!: string

  public picture() {
    return this.pictureUrl || this.picture800Url || this.picture500Url || this.picture250Url || undefined
  }
}

const AnnouncementFeedSchema = z.object({
  feedFlag: z.string(),
  feedId: z.number(),
  feedName: z.string(),
  id: z.number(),
  organizationId: z.number(),
  organizationName: z.string(),
  postId: z.number(),
  siteId: z.number(),
  siteName: z.string()
})

export class AnnouncementFeed {
  constructor(data: z.infer<typeof AnnouncementFeedSchema>) {
    Object.assign(this, data)
  }

  public readonly feedFlag!: string
  public readonly feedId!: number
  public readonly feedName!: string
  public readonly id!: number
  public readonly organizationId!: number
  public readonly organizationName!: string
  public readonly postId!: number
  public readonly siteId!: number
  public readonly siteName!: string
}

const AnnouncementCategorySchema = z.object({
  id: z.number(),
  postId: z.number(),
  category: z.string()
})

export class AnnouncementCategory {
  constructor(data: z.infer<typeof AnnouncementCategorySchema>) {
    Object.assign(this, data)
  }
  public readonly id!: number
  public readonly postId!: number
  public readonly category!: string
}

const AnnouncementSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().optional(),
  homePageBlurb: z.string().optional(),
  youTubeEmbed: z.string().optional(),
  youTubeEmbedPreviewPicture: z.string().optional(),
  postDate: z.coerce.date(),
  endDate: z.string().transform(v => !v || v.startsWith('1/1/1753') ? undefined : new Date(v)),
  startDate: z.coerce.date(),
  createdByName: z.string().optional(),
  likeCount: z.number(),
  pictureInfo: AnnouncementPictureInfoSchema,
  defaultPictureInfo: AnnouncementPictureInfoSchema,
  priority: z.number(),
  priorityEndDate: z.string().transform(v => !v || v.startsWith('1/1/1753') ? undefined : new Date(v)),
  priorityStartDate: z.string().transform(v => !v || v.startsWith('1/1/1753') ? undefined : new Date(v)),
  timeZone: z.string(),
  status: z.string(),
  isDraft: z.boolean(),
  alertType: z.string(),
  categories: z.array(AnnouncementCategorySchema),
  feeds: z.array(AnnouncementFeedSchema)
})

export class Announcement {
  constructor(data: z.infer<typeof AnnouncementSchema>) {
    Object.assign(this, data)
    this.pictureInfo = new AnnouncementPictureInfo(data.pictureInfo)
    this.defaultPictureInfo = new AnnouncementPictureInfo(data.pictureInfo)
    this.categories = data.categories.map(v => new AnnouncementCategory(v))
    this.feeds = data.feeds.map(v => new AnnouncementFeed(v))
  }

  public readonly id!: number
  public readonly title!: string
  public readonly description?: string
  public readonly homePageBlurb?: string
  public readonly youTubeEmbed?: string
  public readonly youTubeEmbedPreviewPicture?: string
  public readonly postDate!: Date
  public readonly endDate?: Date
  public readonly startDate!: Date
  public readonly createdByName?: string
  public readonly likeCount!: number
  public readonly pictureInfo: AnnouncementPictureInfo
  public readonly defaultPictureInfo: AnnouncementPictureInfo
  public readonly priority!: number
  public readonly priorityEndDate?: Date
  public readonly priorityStartDate?: Date
  public readonly timeZone!: string
  public readonly status!: string
  public readonly isDraft!: boolean
  public readonly alertType!: string
  public readonly categories: AnnouncementCategory[]
  public readonly feeds: AnnouncementFeed[]

  public shouldBeDisplayed() {
    const now = new Date()
    return this.startDate <= now && now <= (this.endDate ?? now)
  }

  public showBlurb(){
    if (!this.homePageBlurb) return false;
    if (!this.description) return true;
    if (this.homePageBlurb.toLocaleLowerCase() == this.description.toLocaleLowerCase()
      || this.description.toLocaleLowerCase().includes(this.homePageBlurb.toLocaleLowerCase())) return false;

    const pairs = (s: string) => {
      const arr = [];
      for ( let i = 0; i < s.length; i++) {
        arr.push(s.slice(i, i + 2))
      }
      return arr;
    }

    const blurbPairs = pairs(this.homePageBlurb);
    const descPairs = pairs(this.description);
    const intersection = blurbPairs.filter(p => descPairs.includes(p)).length;

    return ((2 * intersection) / (blurbPairs.length + descPairs.length) > 0.5);
  }

  public formatTimeAgo(locale: Locales) {
    return getTimeFormat(this.postDate, locale)
  }

  public findAssociatedSchool(schools: School[]) {
    return schools.find(s => this.isForSchool(s))
  }

  public isForSchool(school: School) {
    return this.feeds.some(f => f.siteId === school.siteId)
  }

  public findAssociatedDistrict(districts: District[]) {
    return districts.find(d => this.isForDistrict(d))
  }

  public isForDistrict(district: District) {
    return this.feeds.some(f => f.organizationId === district.orgId)
  }

  public picture() {
    return this.pictureInfo.picture() || this.defaultPictureInfo.picture() || undefined
  }
}

export const announcementSchemas = {
  Announcement: AnnouncementSchema,
  AnnouncementCategory: AnnouncementCategorySchema,
  AnnouncementFeed: AnnouncementFeedSchema,
  AnnouncementPictureInfo: AnnouncementPictureInfoSchema
}
